import { DRAFT_FIELD_TYPES_KEY } from "./form-draft-constants";

/**
 * Drops any restored draft value whose field type has changed since the draft was saved —
 * e.g. a builder admin flips a field from Text to Time after a respondent already typed a
 * plain-text answer into it. Without this, a stale value collected under the old type would
 * silently populate the field under its new type on restore: the same class of bug the form
 * builder's own type-change confirmation exists to prevent, just arriving through draft
 * restoration instead of the collected-responses table.
 *
 * A draft saved before this existed has no type snapshot at all, so there's nothing to
 * compare against — those are left untouched rather than dropped, which only means the
 * protection doesn't retroactively apply to a draft already in flight when this shipped.
 *
 * @param draftData - The raw restored draft, including the reserved `DRAFT_FIELD_TYPES_KEY` entry if present.
 * @param currentFieldTypesByLabel - The form's current field types, keyed by label (see `getFieldTypesByLabel`).
 * @returns `draftData` with the type-snapshot key removed and any type-mismatched answers dropped.
 */
export function dropStaleTypedDraftValues(
  draftData: Record<string, unknown>,
  currentFieldTypesByLabel: Record<string, string>
): Record<string, unknown> {
  const rawSavedTypes = draftData[DRAFT_FIELD_TYPES_KEY];
  // No snapshot at all — a draft saved before this existed. Nothing to compare, and no
  // reserved key to strip, so the input is already the correct return value.
  if (!rawSavedTypes || typeof rawSavedTypes !== "object") return draftData;

  const savedTypes = rawSavedTypes as Record<string, unknown>;
  const next: Record<string, unknown> = {};

  for (const [label, value] of Object.entries(draftData)) {
    if (label === DRAFT_FIELD_TYPES_KEY) continue;

    const savedType = savedTypes[label];
    const currentType = currentFieldTypesByLabel[label];
    // A missing snapshot entry, or a field no longer on the form, resolves `savedType` to
    // `undefined` — the `typeof` check below already keeps the value in both cases, so
    // there's no need to test for them separately first.
    if (typeof savedType === "string" && currentType !== undefined && savedType !== currentType) continue;

    next[label] = value;
  }

  return next;
}
